import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import { Spin } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import CommentBox from "../../components/common/CommentBox";
import CommentsSection from "../../components/common/CommentsSection";
import { FilterButton } from "../../assets/icons";
import { getWorkerProgress, addProgressComment } from "../../services/service";
import { IMAGE_BASE_URL } from "../../config/api";

const ProgressDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const progressId = location.state?.progressId;

  const [progressData, setProgressData] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProgressData = async () => {
      if (!progressId) {
        console.log("No progress ID available");
        return;
      }
      
      setLoading(true);
      try {
        const response = await getWorkerProgress({ progress_id: progressId });
        if (response.success) {
          const progress = response.data[0];
          setProgressData(progress);
        } else {
          setError(response.message || "Failed to fetch progress data");
        }
      } catch (err) {
        setError("Failed to fetch progress data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProgressData();
  }, [progressId]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  const renderAttachment = (attachment) => {
    const fileUrl = `${IMAGE_BASE_URL}${attachment.url}`;

    if (attachment.type.startsWith('image/')) {
      return (
        <img
          src={fileUrl}
          alt={`Progress attachment`}
          className="w-full h-[320px] rounded-[4px] object-cover"
        />
      );
    } else if (attachment.type.startsWith('video/')) {
      return (
        <video
          controls
          className="w-full max-h-[360px] rounded-[4px] object-contain bg-black"
        >
          <source src={fileUrl} type={attachment.type} />
          Your browser does not support the video tag.
        </video>
      );
    } else if (attachment.type.startsWith('audio/')) {
      return (
        <div className="w-full rounded-[4px] bg-gray-100 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <p className="text-center mb-4 text-gray-600">{attachment.original_name}</p>
            <audio controls className="w-full">
              <source src={fileUrl} type={attachment.type} />
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>
      );
    }
    return (
      <div className="w-full h-[320px] rounded-[4px] bg-gray-100 flex items-center justify-center p-4">
        <a 
          href={fileUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {attachment.original_name}
        </a>
      </div>
    );
  };

  const handleCommentSubmit = async (comment) => {
    try {
      const response = await addProgressComment({
        progress_id: progressId,
        comment: comment
      });
      
      if (response.success) {
        const updatedProgress = await getWorkerProgress({ progress_id: progressId });
        if (updatedProgress.success) {
          setProgressData(updatedProgress.data[0]);
        }
      }
    } catch (error) {
      console.error("Error submitting comment:", error);
    }
  };

  const handleReplySubmit = async (comment, parentId) => {
    try {
      const response = await addProgressComment({
        progress_id: progressId,
        comment: comment,
        parent_id: parentId
      });
      
      if (response.success) {
        const updatedProgress = await getWorkerProgress({ progress_id: progressId });
        if (updatedProgress.success) {
          setProgressData(updatedProgress.data[0]);
        }
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
    }
  };

  const refreshComments = async () => {
    if (!progressId) return;
    
    try {
      const updatedProgress = await getWorkerProgress({ progress_id: progressId });
      if (updatedProgress.success) {
        setProgressData(updatedProgress.data[0]);
      }
    } catch (error) {
      console.error("Error refreshing comments:", error);
    }
  };

  return (
    <div className="w-full flex flex-col px-2 pb-12">
      <div className="w-full pb-2">
        <ListHeader
          mainHeading="Progress Photos"
          placeholder="Search"
          btnShow={true}
          linkBreadCrum="/view-project"
          linkBreadCrum1="/view-project"
          linkBreadCrum2={`/project-details/${progressData?.Project?.id}`}
          blinkBreadCrumText="Project"
          blinkBreadCrumText1="View Project"
          blinkBreadCrumText2="Project Details"
          blinkBreadCrumText3="Progress Details"
          searchShow={false}
          showLikeFilter={false}
          DropDown={false}
        />
      </div>

      {loading ? (
        <div className="w-full flex justify-center">
          <Spin />
        </div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : (
        <div className="flex gap-5 mt-4">
          {/* Left Section - Images and Comments */}
          <div className="w-[60%] flex flex-col gap-4">
            <div className="bg-white rounded-[8px] p-4">
              <Slider
                {...settings}
                className="rounded-lg overflow-hidden cursor-pointer progress-slider"
              >
                {progressData?.ProgressAttachments?.map((attachment, index) => (
                  <div key={index}>
                    {renderAttachment(attachment)}
                  </div>
                ))}
              </Slider>
            </div>

            <div className="bg-white rounded-[8px] p-4">
              <button
                onClick={() => {
                  navigate("/project-progress", { 
                    state: { 
                      projectId: progressData?.Project?.id,
                      progressId: progressId, 
                      taskId: progressData?.task_id
                    }
                  });
                }}
                className="w-[207px] py-2 rounded-[4px] bg-[#F97316] font-medium text-[20px] text-white"
              >
                Share with Client
              </button>
            </div>

            <div className="bg-white rounded-[8px] p-4">
              <CommentBox worker={true} onSubmit={handleCommentSubmit} />
              <CommentsSection 
                comments={progressData?.ProgressComments || []} 
                onReply={handleReplySubmit}
                onUpdate={refreshComments}
                commentType="wp_comment"
              />
            </div>
          </div>

          {/* Right Section - Progress Details */}
          <div className="w-[40%] bg-white rounded-[8px] p-4">
            <div className="flex flex-col gap-6">
              {/* Worker Info */}
              <div 
                className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-[4px]"
                onClick={() => navigate(`/worker-details/${progressData?.User?.id}`)}
              >
                <img
                  src={progressData?.User?.image ? `${IMAGE_BASE_URL}${progressData.User.image}` : "https://pnghq.com/wp-content/uploads/2023/02/employee-photo-placeholder-png-1000.png"}
                  alt="Worker"
                  className="w-12 h-12 rounded-[4px] object-cover"
                />
                <div>
                  <h3 className="font-medium text-[16px] text-[#4B5563]">
                    {progressData?.User?.username}
                  </h3>
                  <p className="text-[13px] text-[#B7B7B7]">
                    Worker
                  </p>
                </div>
              </div>

              {/* Progress Details */}
              <div className="flex flex-col gap-4">
                <div className="border-b pb-4">
                  <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Progress Title</h4>
                  <p className="text-[14px] text-[#666]">{progressData?.title}</p>
                </div>

                <div className="border-b pb-4">
                  <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Project</h4>
                  <p className="text-[14px] text-[#666]">{progressData?.Project?.title}</p>
                </div>

                <div className="border-b pb-4">
                  <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Task</h4>
                  <p className="text-[14px] text-[#666]">{progressData?.TaskProgress?.title}</p>
                </div>

                <div className="border-b pb-4">
                  <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Location</h4>
                  <p className="text-[14px] text-[#666]">{progressData?.location}</p>
                </div>

                <div className="border-b pb-4">
                  <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Date</h4>
                  <p className="text-[14px] text-[#666]">
                    {new Date(progressData?.date).toLocaleDateString()}
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-[14px] text-[#4B5563] mb-2">Description</h4>
                  <p className="text-[14px] text-[#666]">{progressData?.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressDetailPage;
